// src/v2/leases/LeaseDrawerV2Parts.jsx
//
// Task #9118 (LEASE-DRAWER-V2) — drawer-only Overview/Terms composition.
// Every value reads the live lease read model; no counts, figures or sources
// are invented. Headline facts (rent, size, expiry, next date) live only in
// the header strip; this file renders the non-headline facts.
import React, { useRef, useState } from "react";
import { STAGES, T } from "../../app/tokens.js";
import { PaneHeading } from "../../components/PaneHeading.jsx";
import { InternalLink } from "../../components/InternalLink.jsx";
import { CurrencyToggle } from "../../features/leases/components/CurrencyToggle.jsx";
import { SpacebaseBanner } from "../../features/leases/components/SpacebaseBanner.jsx";
import { ProvenanceBadge } from "../../features/leases/components/ProvenanceBadge.jsx";
import { AreaLifecycleBadge } from "../../features/leases/components/AreaLifecycleBadge.jsx";
import { sourceFor } from "../../features/leases/LeaseSummaryTab.jsx";
import { leaseTypeLabel } from "../../features/leases/leaseTypes.js";
import { leaseAreaPresentation } from "../../features/leases/leaseAreaPresentation.js";
import { recordDateDay, selectRecordDateEvents } from "../../features/leases/recordDateEvents.js";
import { buildLeaseUrl } from "../../services/buildUrl.js";
import { fmtDate, fmtMoney, formatFootprintSqftFull } from "../../utils/format.js";
import { drawerMoney } from "./drawerMoney.js";
import { RecordFactsCard, RecordFactsGrid, RecordFactTile as Fact } from "../../components/RecordFactsCard.jsx";

const card = { background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, padding: "12px 14px", minWidth: 0 };
const label = { fontSize: T.fsXxxs5 ?? T.fsXxs, fontWeight: T.fwBold, color: T.mid, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };
const linkBtn = { background: "none", border: "none", padding: 0, cursor: "pointer", color: T.blue, fontSize: T.fsXs, fontWeight: T.fwBold, fontFamily: "inherit" };
const DAY = 864e5;
const monYear = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", { month: "short", year: "numeric" });

/** Pure runway model from the shared date selector (same source as the strip). */
export function runwayModel(lease, criticalDates, today = new Date()) {
  const sel = selectRecordDateEvents(lease, criticalDates, today);
  const start = sel.events.find((e) => e.type === "commencement")?.date ?? null;
  const end = sel.events.find((e) => e.type === "expiration")?.date ?? null;
  const rawEnd = String(lease?.endDate ?? lease?.leaseEnd ?? "");
  const openEnded = !end && /^9999/.test(rawEnd);
  const t = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const ms = (d) => Date.parse(`${d}T00:00:00Z`);
  let state = "unknown";
  let elapsed = null;
  let yearsLeft = null;
  if (start && end && ms(end) > ms(start)) {
    if (t < start) state = "future";
    else if (t > end) state = "expired";
    else state = "active";
    elapsed = Math.round(Math.min(1, Math.max(0, (ms(t) - ms(start)) / (ms(end) - ms(start)))) * 100);
    yearsLeft = Math.max(0, (ms(end) - ms(t)) / DAY / 365.25);
  } else if (openEnded) state = "open";
  const pos = (d) => (start && end && state !== "unknown" ? Math.min(100, Math.max(0, ((ms(d) - ms(start)) / (ms(end) - ms(start))) * 100)) : null);
  const next = sel.next && sel.next.type !== "expiration" && sel.next.type !== "commencement" ? sel.next : null;
  return { start, end, today: t, state, elapsed, yearsLeft, next, nextPos: next ? pos(next.date) : null, blocked: Boolean(sel.blocked),
    soonest: !end && lease?.soonestPossibleExpiration ? String(lease.soonestPossibleExpiration).slice(0, 10) : null };
}

export function LeaseRunway({ lease, criticalDates, today = new Date(), readState = "ready" }) {
  const m = runwayModel(lease, criticalDates, today);
  if (readState !== "ready") return <div role="status">{readState === "loading" ? "Loading critical dates…" : "Critical dates couldn't be loaded."}</div>;
  const fill = m.elapsed ?? 0;
  const head = m.state === "expired" ? <span style={{ color: T.ragRed ?? T.red }}>Expired {fmtDate(m.end)}</span>
    : m.state === "future" ? <>Starts {fmtDate(m.start)}</>
      : m.state === "active" ? <>{fill}% elapsed · {m.yearsLeft.toFixed(1)} yrs left</>
        : m.state === "open" ? <>Open-ended{m.start ? ` · since ${fmtDate(m.start)}` : ""}</>
          : <>Term dates not fully recorded</>;
  return (
    <div data-testid="lease-runway" data-state={m.state} style={{ marginTop: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: T.fsXxs, color: T.mid, marginBottom: 5 }}>
        <span data-testid="lease-runway-start">{m.start ? monYear(m.start) : "Start —"}</span>
        <span data-testid="lease-runway-status" style={{ fontWeight: T.fwBold, color: T.dark }}>{head}</span>
        <span data-testid="lease-runway-end">{m.end ? monYear(m.end) : m.state === "open" ? "No fixed end" : "End —"}</span>
      </div>
      <div aria-hidden="true" style={{ position: "relative", height: 6, borderRadius: 999, background: T.lightGray }}>
        {m.state !== "unknown" && m.state !== "open" && (
          <div style={{ position: "absolute", inset: 0, borderRadius: 999, background: m.state === "expired" ? T.ragRed ?? T.red : T.blue, transformOrigin: "left", transform: `scaleX(${fill / 100})` }} />
        )}
        {(m.state === "active") && (
          <span data-testid="lease-runway-today" title={`Today ${fmtDate(m.today)}`} style={{ position: "absolute", top: -3, left: `calc(${fill}% - 1px)`, width: 2, height: 12, background: T.dark, borderRadius: 1 }} />
        )}
        {m.nextPos != null && (
          <span data-testid="lease-runway-notice" style={{ position: "absolute", top: -3, left: `calc(${m.nextPos}% - 5px)`, width: 10, height: 12, borderRadius: 3, background: T.amber ?? T.warmBeige, border: `1px solid ${T.dark}` }} />
        )}
      </div>
      {(m.next || m.soonest || m.blocked) && (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 6, fontSize: T.fsXxs, color: T.mid }}>
          {m.next && (
            <span data-testid="lease-runway-next" title={m.next.disclosure || m.next.label}>
              Next: {m.next.label} {m.next.provenance === "calculated" ? monYear(m.next.date) : fmtDate(m.next.date)}
              {m.next.provenance === "calculated" && <span data-testid="lease-runway-calc" style={{ marginLeft: 5, padding: "0 5px", borderRadius: 4, border: `1px dotted ${T.mid}`, fontWeight: T.fwBold }}>calc</span>}
               {m.next.provenance === "ambiguous" && <span title={m.next.disclosure} style={{ marginLeft: 5, padding: "0 5px", borderRadius: 4, border: `1px dotted ${T.mid}` }}>unverified</span>}
            </span>
          )}
          {m.blocked && <span data-testid="lease-runway-unverified">Date sources need review</span>}
          {m.soonest && <span data-testid="lease-runway-soonest">Soonest possible expiration {fmtDate(m.soonest)}</span>}
        </div>
      )}
    </div>
  );
}

/** Drawer-only non-headline terms; annual rent remains in the header strip. */
export function DrawerKeyTermsPane({ lease, computed, displayCcy, rates, onDisplayCcyChange, today, criticalDates, recordStatus, canWrite, onEdit, dateReadState = "ready" }) {
  const nativeCcy = lease?.currency || "USD";
  const asOf = today.toLocaleDateString("en-US", { month: "short", year: "numeric" });
  const startDay = recordDateDay(lease?.startDate);
  const endDay = recordDateDay(lease?.endDate);
  const startFmt = startDay ? monYear(startDay) : null;
  const endFmt = endDay ? monYear(endDay) : String(lease?.endDate ?? "").startsWith("9999") ? "Ongoing" : null;
  const months = computed?.termLengthMonths;
  const termValue = [lease?.termType || null, months ? `~${Math.round(months / 12)} yrs` : null].filter(Boolean).join(" · ") || null;
  const renewal = lease?.renewalCount != null && lease.renewalCount !== ""
    ? (lease.renewalTermYears ? `${lease.renewalCount} × ${lease.renewalTermYears}-yr` : String(lease.renewalCount)) : null;
  const depNative = lease?.securityDepositAmount != null && lease.securityDepositAmount !== "" ? parseFloat(lease.securityDepositAmount) : null;
  const dep = drawerMoney(depNative, nativeCcy, displayCcy, rates, { compact: true });
  const deposit = depNative != null ? dep.text : null;
  const depositTooltip = [fmtMoney(dep.amount, dep.currency), dep.tooltip].filter(Boolean).join(" · ");
  const baseYear = lease?.baseYear != null && lease.baseYear !== "" ? `Base year ${lease.baseYear}` : null;

  return (
    <RecordFactsCard prefix="key-terms" title="Key terms" asOf={asOf} actions={<>
        <CurrencyToggle nativeCurrency={nativeCcy} value={displayCcy} onChange={onDisplayCcyChange} />
        {canWrite && <button type="button" data-testid="key-terms-edit-lease" onClick={onEdit} style={linkBtn}>Edit lease</button>}
      </>}>
      <RecordFactsGrid prefix="key-terms">
        <Fact testId="status" title="Status"><div data-testid="key-terms-status">{recordStatus}</div></Fact>
        <Fact testId="type" title="Type" value={leaseTypeLabel(lease?.type) || null} source={sourceFor(lease, "type")} />
        <Fact testId="structure" title="Structure" value={lease?.opexStructure || null} source={sourceFor(lease, "opexStructure")} sub={baseYear} />
        <Fact testId="term" title="Term" value={termValue} source={sourceFor(lease, "termType")} sub={[startFmt, endFmt].filter(Boolean).join(" – ") || null} />
        <Fact testId="renewals" title="Renewals" value={renewal} source={sourceFor(lease, "renewalCount")} sub={renewal ? "option" : null} />
        <Fact testId="deposit" title="Deposit" value={deposit} tooltip={depositTooltip} source={sourceFor(lease, "securityDepositAmount")} sub={lease?.securityDepositType || null} />
        <Fact testId="lessor" title="Lessor" value={lease?.vendor || null} source={sourceFor(lease, "vendor")} />
      </RecordFactsGrid>
      <LeaseRunway lease={lease} criticalDates={criticalDates} today={today} readState={dateReadState} />
      <div data-testid="key-terms-source-footer" style={{ marginTop: 10, paddingTop: 8, borderTop: `1px solid ${T.border}`, display: "flex", flexWrap: "wrap", overflowWrap: "anywhere", minWidth: 0 }}>
        <SpacebaseBanner leaseIdSpacebase={lease?.leaseIdSpacebase} lastCsvImportedAt={lease?.lastCsvImportedAt} />
      </div>
    </RecordFactsCard>
  );
}

/** Premises: linked office card + active areas; no photo, no map. */
export function DrawerPremisesPane({ location, lease, siblingLeases = [], today = new Date(), onOpenOffice, onEditAreas, locationError = false }) {
  const [showOther, setShowOther] = useState(false);
  const locationId = location?.id || lease?.locationId || null;
  const areas = Array.isArray(lease?.areas) ? lease.areas : [];
  const life = leaseAreaPresentation({ areas }, today);
  const other = life.groups.filter((g) => g.status !== "active");
  const otherCount = other.reduce((n, g) => n + g.areas.length, 0);
  const siblings = siblingLeases.filter((l) => l?.id && l.id !== lease?.id);
  const stage = location?.stage || lease?.locationStage || "";
  const stageLabel = STAGES[stage]?.label || stage || "Stage unavailable";
  const name = location?.locationAlias || lease?.locationAlias || null;
  const market = location?.market || lease?.city || null;
  const sf = (a) => (a?.sizeSf != null && Number.isFinite(Number(a.sizeSf)) ? formatFootprintSqftFull(Number(a.sizeSf)) : "—");

  return (
    <section data-testid="lease-premises-pane" style={card}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
        <PaneHeading style={{ marginBottom: 0, flex: 1 }}>Premises</PaneHeading>
        {onEditAreas && areas.length > 0 && <button type="button" data-testid="premises-edit-areas" onClick={onEditAreas} style={linkBtn}>Edit areas</button>}
      </div>
      {locationId ? (
        <div data-testid="lease-office-tieback" style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "9px 11px", background: T.tint, border: `1px solid ${T.border}`, borderRadius: 8 }}>
          <span style={{ minWidth: 0, flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
            <span data-testid="lease-office-identity" style={{ color: T.dark, fontSize: T.fsSm, fontWeight: T.fwBold }}>
              {location?.isHub && <span data-testid="premises-hub" style={{ color: T.hubChipText ?? T.blue, marginRight: 6 }}>{"\u25c6"} Hub</span>}
              {[market, name].filter(Boolean).join(" · ") || locationId}
              {name && <ProvenanceBadge source={sourceFor(lease, "locationAlias")} />}
            </span>
            <span style={{ color: T.mid, fontSize: T.fsXxs }}>{locationId} · {stageLabel}</span>
            {location?.address
              ? <span data-testid="premises-address" style={{ color: T.mid, fontSize: T.fsXs }}>{location.address}</span>
              : <span data-testid="premises-address-missing" style={{ color: T.light, fontSize: T.fsXs }}>{locationError ? "Office details couldn't be loaded" : "No address recorded"}</span>}
          </span>
          {onOpenOffice && <button type="button" data-testid="lease-open-office-link" onClick={() => onOpenOffice(locationId)} style={{ ...linkBtn, whiteSpace: "nowrap" }}>Open office →</button>}
        </div>
      ) : (
        <div data-testid="premises-unlinked" style={{ fontSize: T.fsXs, color: T.mid, padding: "8px 0" }}>This lease isn't linked to an office.</div>
      )}

      <div data-testid="premises-active-areas" style={{ marginTop: 10 }}>
        <div style={label}>{life.activeCount} active {life.activeCount === 1 ? "area" : "areas"}</div>
        {life.active.length === 0 && <div style={{ fontSize: T.fsXs, color: T.light }}>{areas.length ? "No active areas" : "Area details unavailable"}</div>}
        {life.active.map((a, i) => (
          <div key={`${a?.id || a?.name || "a"}-${i}`} data-testid="premises-area-row" style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: i < life.active.length - 1 ? `1px solid ${T.border}` : 0, fontSize: T.fsXs }}>
            <span aria-label="Active" data-testid="premises-area-dot" style={{ width: 7, height: 7, borderRadius: 999, background: T.forestGreen ?? T.blue, flexShrink: 0 }} />
            <span style={{ flex: 1, minWidth: 0, color: T.dark, fontWeight: T.fwSemibold }}>{a?.name?.trim() || "Area"}
              {(a?.deskCount ?? a?.desks) != null && (a?.deskCount ?? a?.desks) !== "" && <span data-testid="lease-area-desk-count" style={{ color: T.mid, fontWeight: T.fwRegular }}> · {a?.deskCount ?? a?.desks} desks</span>}</span>
            <span style={{ color: T.dark, fontWeight: T.fwSemibold, whiteSpace: "nowrap" }}>{sf(a)}
              {a.usableSizeSf != null && <span style={{ color: T.mid, fontWeight: T.fwRegular }}> · {Number(a.usableSizeSf).toLocaleString()} usable sf</span>}</span>
          </div>
        ))}
      </div>

      {otherCount > 0 && (
        <div style={{ marginTop: 8 }}>
          <button type="button" data-testid="premises-other-toggle" aria-expanded={showOther} aria-controls="premises-other-areas" onClick={() => setShowOther((v) => !v)} style={linkBtn}>
            Other recorded areas ({otherCount}) {showOther ? "\u25B4" : "\u25BE"}
          </button>
          {showOther && (
            <div id="premises-other-areas" data-testid="premises-other-areas" style={{ marginTop: 4 }}>
              {other.map((g) => (
                <React.Fragment key={g.status}>
                  <div style={{ color: T.light, fontSize: T.fsXxs, fontWeight: T.fwSemibold, padding: "6px 0 2px" }}>{g.label}</div>
                  {g.areas.map((a, i) => {
                    const desks = a?.deskCount ?? a?.desks;
                    return (
                      <div key={`${a?.id || a?.name || "o"}-${i}`} data-testid="premises-other-area-row" style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "4px 0", fontSize: T.fsXs, color: T.light }}>
                        <span>{a?.name?.trim() || "Area"}<AreaLifecycleBadge area={a} />{desks != null && desks !== "" ? ` · ${desks} desks` : ""}</span>
                        <span>{sf(a)}{a?.usableSizeSf != null && ` · ${Number(a.usableSizeSf).toLocaleString()} usable sf`}</span>
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      )}
      {siblings.length > 0 && (
        <div data-testid="premises-sibling-leases" style={{ marginTop: 8, fontSize: T.fsXs, color: T.mid }}>
          Separate leases here:{" "}
          {siblings.map((s, i) => (
            <React.Fragment key={s.id}>{i > 0 && " · "}
              <InternalLink data-testid="lease-areas-sibling-link" href={buildLeaseUrl(s.id)} style={{ color: T.blue, fontWeight: T.fwSemibold, textDecoration: "none" }}>{s.id} →</InternalLink>
            </React.Fragment>
          ))}
        </div>
      )}
    </section>
  );
}

const LEGEND = [
  { source: "spacebase", label: "Spacebase" }, { source: "computed", label: "Calculated" },
  { source: "manual", label: "Manual" }, { source: "pdf_abstraction", label: "PDF" },
];

/**
 * Terms tab: jump chips → legend → Parties + term-group accordions (open by
 * default). `sections` = [{ key, title, count?, render() }].
 */
export function DrawerTermsPane({ sections }) {
  const [open, setOpen] = useState(() => Object.fromEntries(sections.map((s) => [s.key, true])));
  const refs = useRef({});
  const jump = (key) => {
    setOpen((o) => ({ ...o, [key]: true }));
    const go = () => {
      const el = refs.current[key];
      if (!el) return;
      el.scrollIntoView?.({ block: "start", behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ? "auto" : "smooth" });
      el.querySelector("[data-terms-heading]")?.focus?.({ preventScroll: true });
    };
    if (typeof requestAnimationFrame === "function") requestAnimationFrame(go); else go();
  };
  return (
    <div data-testid="lease-terms-pane" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <nav aria-label="Jump to term group" data-testid="terms-jump-chips" style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {sections.map((s) => (
          <button key={s.key} type="button" data-testid={`terms-jump-${s.key}`} onClick={() => jump(s.key)}
            style={{ border: `1px solid ${T.border}`, background: T.card, borderRadius: 999, padding: "4px 11px", fontSize: T.fsXs, fontWeight: T.fwBold, color: T.dark, cursor: "pointer", fontFamily: "inherit" }}>
            {s.title}{s.count ? <span style={{ color: T.mid, fontWeight: T.fwSemibold }}> · {s.count}</span> : null}
          </button>
        ))}
      </nav>
      <div data-testid="terms-provenance-legend" style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: T.fsXxs, color: T.light }}>
        {LEGEND.map(({ source, label: l }) => <span key={source} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><ProvenanceBadge source={source} />{l}</span>)}
      </div>
      {sections.map((s) => {
        const isOpen = open[s.key] !== false;
        return (
          <section key={s.key} ref={(el) => { refs.current[s.key] = el; }} data-testid={`terms-section-${s.key}`} style={{ ...card, padding: 0, scrollMarginTop: 64 }}>
            <h3 style={{ margin: 0 }}>
              <button type="button" data-terms-heading id={`terms-head-${s.key}`} data-testid={`terms-accordion-${s.key}`} aria-expanded={isOpen} aria-controls={`terms-body-${s.key}`}
                onClick={() => setOpen((o) => ({ ...o, [s.key]: !isOpen }))}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "11px 14px", background: "none", border: 0, cursor: "pointer", textAlign: "left", fontFamily: "inherit", fontSize: T.fsSm, fontWeight: T.fwBold, color: T.dark }}>
                <span style={{ flex: 1 }}>{s.title}</span>
                {s.count && <span data-testid={`terms-accordion-count-${s.key}`} style={{ fontSize: T.fsXxs, color: T.mid, fontWeight: T.fwBold }}>{s.count}</span>}
                <span aria-hidden="true" style={{ color: T.mid, transform: isOpen ? "none" : "rotate(-90deg)", transition: "transform .15s" }}>{"\u25BE"}</span>
              </button>
            </h3>
            <div id={`terms-body-${s.key}`} role="region" aria-labelledby={`terms-head-${s.key}`} hidden={!isOpen} style={{ padding: "0 14px 12px" }}>
              {isOpen && s.render()}
            </div>
          </section>
        );
      })}
    </div>
  );
}
